<template>
  <v-container fluid>
    <v-row>
      <v-col>
        <v-card>
          <v-card>
            <v-toolbar :dark="$vuetify.theme.dark" color="primary">
              <v-toolbar-title class="textwithbackground--text">
                {{ title }}
              </v-toolbar-title>
            </v-toolbar>
            <v-card-text>
              <general-error v-if="!!generalError" />
              <v-progress-linear :active="isLoading" color="loader" indeterminate />
              <user-registration-request-view :profile-info="userProfile">
                <template v-slot:additional>
                  <v-expansion-panel v-test="{ panel: '' }">
                    <v-expansion-panel-header>
                      <div id="attach_file">
                        <v-icon>
                          attach_file
                        </v-icon>
                        {{ $t('Прикрепленные документы') }}
                      </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content eager>
                      <identity-document-list-view
                          :request-files="requestFiles"
                          :document-types="requiredDocumentTypes"
                          :loading="isLoading" />
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </template>
              </user-registration-request-view>
              <v-row>
                <v-col class="mt-3">
                  <ValidationObserver ref="comment">
                    <validatable-textarea
                        v-if="inprocess || request.processComment"
                        v-model="processComment"
                        :readonly="!inprocess"
                        :label="$t('Комментарий')"
                        :rules="{ required: rejected && inprocess }" />
                  </ValidationObserver>
                </v-col>
              </v-row>
            </v-card-text>
            <v-card-actions>
              <v-row>
                <v-col class="text-left">
                  <btn-icon-tip
                      :tip="$t('К списку запросов')"
                      :icon="$vuetify.breakpoint.xsOnly
                        ? 'reply'
                        : ''"
                      :icon-color="$vuetify.breakpoint.xsOnly
                        ? 'primary'
                        : ''"
                      :btn-color="$vuetify.breakpoint.smAndUp
                        ? 'primary'
                        : ''"
                      @click="goToRoute(`${urlRootPath}/userRegistrationRequests/${request.status}`)" />
                </v-col>
                <v-col
                    class="text-right">
                  <btn-icon-tip
                      v-if="inprocess"
                      id="reject"
                      :btn-color="$vuetify.breakpoint.smAndUp
                        ? 'error'
                        : ''"
                      :icon-color="$vuetify.breakpoint.xsOnly
                        ? 'error'
                        : ''"
                      :icon="$vuetify.breakpoint.xsOnly
                        ? 'highlight_off'
                        : ''"
                      :tip="$t('Отклонить')"
                      :disabled="isLoading"
                      @click="reject" />
                </v-col>
                <v-col
                    class="text-right"
                    cols="3"
                    lg="2"
                    md="2"
                    sm="3">
                  <btn-icon-tip
                      v-if="inprocess"
                      id="approve"
                      :btn-color="$vuetify.breakpoint.smAndUp
                        ? 'success'
                        : ''"
                      :icon-color="$vuetify.breakpoint.xsOnly
                        ? 'success'
                        : ''"
                      :icon="$vuetify.breakpoint.xsOnly
                        ? 'check_circle'
                        : ''"
                      :tip="$t('Одобрить')"
                      :disabled="isLoading"
                      @click="approve" />
                </v-col>
              </v-row>
            </v-card-actions>
          </v-card>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import BtnIconTip from '@/components/reused/ui/BtnIconTip.vue'
  import ValidatableTextarea from '@/components/reused/ui/ValidatableTextarea.vue'
  import UserRegistrationRequestView from '@/components/reused/user/UserRegistrationRequestView.vue'
  import IdentityDocumentListView from '@/components/reused/files/IdentityDocumentListView.vue'
  import GeneralError from '@/components/reused/common/GeneralError.vue'
  import { REQUEST_STATUSES, IDENTITY_REQUEST_TYPES } from '@/constants/business'
  import { mapActions, mapState } from 'vuex'
  import { USER_REQUESTS, IDENTITY_FILES } from '@/constants/store.js'

  export default {
    components: {
      BtnIconTip,
      ValidatableTextarea,
      UserRegistrationRequestView,
      IdentityDocumentListView,
      GeneralError
    },
    props: {
      requestId: {
        type: String,
        required: true
      }
    },
    data () {
      return {
        isLoading: false,
        processComment: null,
        rejected: false
      }
    },
    computed: {
      ...mapState(USER_REQUESTS.NAMESPACE, {
        request: state => (state[USER_REQUESTS.STATE.CURRENT_REQUEST] || {})
      }),
      ...mapState(IDENTITY_FILES.NAMESPACE, {
        requiredDocumentTypes: state => (state[IDENTITY_FILES.STATE.REQUIRED_DOCUMENT_TYPES] || [])
      }),
      title () {
        return this.$t(this.inprocess
                         ? 'Обработка запроса на регистрацию пользователя в организации {0}'
                         : 'Просмотр запроса на регистрацию пользователя в организации {0}',
                       [this.request.organizationName])
      },
      requestType () {
        return IDENTITY_REQUEST_TYPES.ORGANIZATION_AGENT
      },
      userProfile () {
        return this.request.userProfile || {}
      },
      inprocess () {
        return this.request.status === REQUEST_STATUSES.INPROCESS
      },
      requestFiles () {
        return this.request.files || []
      }
    },
    created () {
      const vm = this
      vm.clearStore().then(() => {
        vm.getUserRequestInfo()
      })
    },
    beforeDestroy () {
      this.clearStore()
    },
    methods: {
      ...mapActions(USER_REQUESTS.NAMESPACE, {
        getUserRequestInfoAction: USER_REQUESTS.ACTIONS.GET_USER_REQUEST_INFO,
        processUserRegistrationRequestAction: USER_REQUESTS.ACTIONS.PROCESS_USER_REGISTRATION_REQUEST,
        clearUserRequestInfoAction: USER_REQUESTS.ACTIONS.CLEAR_USER_REQUEST_INFO
      }),
      ...mapActions(IDENTITY_FILES.NAMESPACE, {
        getRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.GET_REQUIRED_DOCUMENT_TYPES,
        clearRequiredDocumentTypesAction: IDENTITY_FILES.ACTIONS.CLEAR_REQUIRED_DOCUMENT_TYPES
      }),
      clearStore: async function () {
        await Promise.all(
          [
            this.clearUserRequestInfoAction(),
            this.clearRequiredDocumentTypesAction()
          ])
      },
      getUserRequestInfo: async function () {
        try {
          this.isLoading = true
          await this.getUserRequestInfoAction({
            requestId: this.requestId
          })
          this.processComment = this.request.processComment

          await this.getRequiredDocumentTypesAction({
            requestType: this.requestType
          })
          this.isLoading = false
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      },
      approve: function () {
        const vm = this
        vm.rejected = false
        // nextTick: действия после перерисовки разметки (проверка полей)

        vm.$nextTick(() => {
          vm.processUserRegistrationRequest()
        })
      },
      reject: function () {
        const vm = this
        vm.rejected = true
        vm.$nextTick(() => {
          vm.processUserRegistrationRequest()
        })
      },
      processUserRegistrationRequest: async function () {
        const result = await this.$refs.comment.validate()
        if (!result) {
          return
        }
        try {
          this.isLoading = true
          await this.processUserRegistrationRequestAction({
            requestId: this.requestId,
            process: {
              userId: this.request.userId,
              processComment: this.processComment,
              status: this.rejected
                ? REQUEST_STATUSES.REJECTED
                : REQUEST_STATUSES.APPROVED
            }
          })
          this.isLoading = false
          this.goToRoute(`${this.urlRootPath}/userRegistrationRequests/${this.request.status}`)
        } catch (error) {
          this.isLoading = false
          this.throwIfNotServerError(error)
        }
      }
    }
  }
</script>
